// src/components/ListaVendedores.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  listarVendedoresPaginado,
  excluirVendedor,
  listarRevendasDropdown,
} from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Edit,
  Trash2,
  Search,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUpDown,
} from "lucide-react";

const HeaderSortButton = ({ label, field, sortBy, desc, onSort }) => {
  const active = sortBy === field;

  return (
    <button
      type="button"
      className={`
        inline-flex items-center gap-1 transition
        ${active ? "text-blue-600 dark:text-blue-300" : "text-gray-600 dark:text-slate-200"}
        hover:text-blue-700 dark:hover:text-blue-200
      `}
      onClick={() => onSort(field)}
      title="Ordenar"
    >
      {label} <ArrowUpDown size={16} />
      {active && <span className="text-xs ml-1 opacity-80">{desc ? "↓" : "↑"}</span>}
    </button>
  );
};

const moneyOrDash = (v) => {
  if (v === null || v === undefined) return "-";
  // comissão costuma ser percentual. se for 5 => "5,00%"
  try {
    return `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  } catch {
    return `${v}%`;
  }
};

const ListaVendedores = ({ token }) => {
  const navigate = useNavigate();

  // URL params (igual ZenTag)
  const [searchParams, setSearchParams] = useSearchParams();
  const qp = (k, def = "") => searchParams.get(k) ?? def;

  // filtros
  const [busca, setBusca] = useState(qp("search", ""));
  const [revendaId, setRevendaId] = useState(qp("revendaId", "")); // "" | "0" | "123"

  // dropdown revendas
  const [revendas, setRevendas] = useState([]);

  // paginação/ordenação
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(Number(qp("page", "1")) || 1);
  const [pageSize, setPageSize] = useState(Number(qp("pageSize", "60")) || 60);

  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const [sortBy, setSortBy] = useState(qp("sortBy", "nome"));
  const [desc, setDesc] = useState(qp("desc", "") === "true");

  // debounce busca
  const [searchTerm, setSearchTerm] = useState(qp("search", ""));
  const debounceRef = useRef(null);

  // carrega dropdown revendas (1x)
  const fetchRevendas = async () => {
    try {
      const r = await listarRevendasDropdown(token);
      const data = r?.data;

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.Items)
        ? data.Items
        : [];

      setRevendas(list);
    } catch (e) {
      console.error("Erro ao listar revendas (dropdown):", e);
      setRevendas([]);
    }
  };

  useEffect(() => {
    fetchRevendas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // sincroniza filtros/paginação na URL
  useEffect(() => {
    const p = new URLSearchParams(searchParams);

    if (busca) p.set("search", busca);
    else p.delete("search");

    if (revendaId) p.set("revendaId", revendaId);
    else p.delete("revendaId");

    p.set("page", String(page));
    p.set("pageSize", String(pageSize));

    if (sortBy) p.set("sortBy", sortBy);
    if (desc) p.set("desc", "true");
    else p.delete("desc");

    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, revendaId, page, pageSize, sortBy, desc]);

  const fetchData = async (opts = {}) => {
    const res = await listarVendedoresPaginado(
      {
        page,
        pageSize,
        search: searchTerm,
        sortBy,
        desc,
        revendaId: revendaId === "" ? undefined : Number(revendaId),
        ...opts,
      },
      token
    );

    const data = res.data;
    setItems(data.items || []);
    setPage(data.page);
    setPageSize(data.pageSize);
    setTotalItems(data.totalItems);
    setTotalPages(data.totalPages);
    setHasPrevious(data.hasPrevious);
    setHasNext(data.hasNext);
  };

  useEffect(() => {
    fetchData().catch((err) => console.error("Erro ao listar vendedores:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTerm, sortBy, desc, revendaId]);

  const handleBuscaChange = (e) => {
    const valor = e.target.value;
    setBusca(valor);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchTerm(valor);
    }, 400);
  };

  const handleRevendaChange = (e) => {
    setRevendaId(e.target.value);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) setDesc((d) => !d);
    else {
      setSortBy(field);
      setDesc(false);
    }
    setPage(1);
  };

  const handleEditar = (id) => navigate(`/vendedores/editar/${id}`);
  const handleNovo = () => navigate(`/vendedores/novo`);

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este vendedor?")) return;
    try {
      await excluirVendedor(id, token);
      await fetchData();
      alert("Vendedor excluído com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir vendedor:", error);
      alert("Erro ao excluir vendedor!");
    }
  };

  const paginaInfo = useMemo(() => {
    const ini = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const fim = Math.min(page * pageSize, totalItems);
    return `${ini}-${fim} de ${totalItems}`;
  }, [page, pageSize, totalItems]);

  const revendaLabel = useMemo(() => {
    if (!revendaId) return "";
    if (revendaId === "0") return "MASTER";

    const r = revendas.find((x) => String(x.id) === String(revendaId));
    return r?.nomeFantasia || r?.razaoSocial || `Revenda ${revendaId}`;
  }, [revendaId, revendas]);

  return (
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      {/* topo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Listagem de Vendedores
          </h2>

          {revendaId && (
            <p className="text-xs text-gray-500 dark:text-slate-300/80 mt-1">
              Filtrando por revenda: <b>#{revendaId}</b>
              {revendaLabel ? ` - ${revendaLabel}` : ""}
            </p>
          )}
        </div>

        <button
          onClick={handleNovo}
          className="
            inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition
            bg-blue-600 text-white hover:bg-blue-500
          "
        >
          + Novo Vendedor
        </button>
      </div>

      {/* filtros */}
      <div className="rounded-2xl p-4 mb-6 zen-card border-gray-200 dark:bg-white/5 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {/* busca */}
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 pointer-events-none"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar por Nome"
                value={busca}
                onChange={handleBuscaChange}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>
          </div>

          {/* revenda */}
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Revenda
            </label>
            <select
              value={revendaId}
              onChange={handleRevendaChange}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
            >
              <option value="">Todas</option>
              <option value="0">MASTER</option>

              {revendas.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.nomeFantasia || r.razaoSocial || `Revenda ${r.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* page size */}
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Itens por página
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
            >
              {[10, 20, 30, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/página
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* tabela */}
      <div className="overflow-x-auto">
        <div className="min-w-full rounded-2xl zen-card dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Id" field="id" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>

                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Nome" field="nome" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>

                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Comissão" field="comissao" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>

                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Revenda" field="revenda" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>

                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                  Ações
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-700 dark:text-slate-200">
              {items.map((v) => (
                <tr
                  key={v.id}
                  className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                >
                  <td className="py-3 px-4">{v.id}</td>
                  <td className="py-3 px-4">{v.nome}</td>
                  <td className="py-3 px-4">{moneyOrDash(v.percentualComissao)}</td>
                  <td className="py-3 px-4">{v.revendaNome || v.revendaId}</td>

                  <td className="text-center py-3 px-4">
                    <div className="inline-flex items-center gap-1">
                      <button
                        className="p-2 rounded-full transition text-blue-600 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-500/10"
                        onClick={() => handleEditar(v.id)}
                        title="Editar"
                      >
                        <Edit size={18} />
                      </button>

                      <button
                        className="p-2 rounded-full transition text-red-600 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-500/10"
                        onClick={() => handleExcluir(v.id)}
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {items.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-10 text-center text-gray-500 dark:text-slate-300/80">
                    Nenhum vendedor encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* paginação */}
      <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <span className="text-sm text-gray-600 dark:text-slate-300/80">{paginaInfo}</span>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setPage(1)}
            disabled={!hasPrevious}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Primeira"
          >
            <ChevronsLeft size={18} />
          </button>

          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!hasPrevious}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Anterior"
          >
            <ChevronLeft size={18} />
          </button>

          <span className="px-2 text-sm text-gray-700 dark:text-slate-200">
            Página <b>{page}</b> de <b>{totalPages}</b>
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={!hasNext}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Próxima"
          >
            <ChevronRight size={18} />
          </button>

          <button
            onClick={() => setPage(totalPages)}
            disabled={!hasNext}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Última"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListaVendedores;
